import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * Supports fabric mode display:
 * - 50cm: qty 1 = 0,5m, qty 2 = 1m, etc.
 * - 1m: qty 2 = 1m, qty 4 = 2m, etc.
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /** @type {string|null} */
  #fabricMode = null;

  /** @type {HTMLInputElement|null} */
  #displayInput = null;

  connectedCallback() {
    super.connectedCallback();

    // Check for fabric mode
    this.#fabricMode = this.dataset.fabricMode || null;
    this.#displayInput = /** @type {HTMLInputElement|null} */ (this.querySelector('[ref="displayInput"]'));

    // Initial display update
    if (this.#fabricMode && this.#displayInput) {
      this.#updateDisplayValue();
    }
  }

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /**
   * Override updateQuantity to also update the display value for fabric mode
   * @param {number} stepMultiplier - Positive for increase, negative for decrease
   */
  updateQuantity(stepMultiplier) {
    super.updateQuantity(stepMultiplier);

    if (this.#fabricMode && this.#displayInput) {
      this.#updateDisplayValue();
    }
  }

  /**
   * Updates the display input value based on fabric mode (number only)
   * - 50cm: qty 1 = 0.5, qty 2 = 1, etc.
   * - 1m: qty 1 = 1, qty 2 = 2, etc.
   */
  #updateDisplayValue() {
    if (!this.#displayInput) return;

    const { value } = this.getCurrentValues();
    let displayValue;

    if (this.#fabricMode === '50cm') {
      // qty 1 = 0.5, qty 2 = 1, qty 3 = 1.5
      displayValue = value * 0.5;
    } else if (this.#fabricMode === '1m') {
      // qty 1 = 1, qty 2 = 2, qty 3 = 3
      displayValue = value;
    } else {
      displayValue = value;
    }

    this.#displayInput.value = displayValue.toString();
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
